import axios from 'axios'

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
api.interceptors.response.use(
  response => {
    // A service that needs response headers (e.g. the lease PDF's
    // X-Document-Hash) asks for the whole response with `rawResponse`.
    if (response.config?.rawResponse) return response
    return response.data
  },
  error => {
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response

      // Unauthorized - clear token and let ProtectedRoute handle the redirect
      // Do NOT force a hard redirect here; public pages should remain accessible
      if (status === 401) {
        localStorage.removeItem('authToken')
      }

      // Forbidden
      if (status === 403) {
        console.error('Access forbidden:', data.error?.message)
      }

      // Validation errors
      if (status === 400 || status === 422) {
        const message = data?.error?.message || 'Validation error'
        const err = new Error(message)
        // Keep the server's code and details (e.g. the flagged words of a
        // fair-housing check) for pages that show more than the sentence.
        err.status = status
        if (data?.error?.code) err.code = data.error.code
        if (data?.error) err.details = data.error
        return Promise.reject(err)
      }

      // A dependency the server relies on failed (email, uploads): the
      // server's own sentence says what to do instead, so pass it on.
      if ((status === 502 || status === 503) && data?.error?.message) {
        return Promise.reject(new Error(data.error.message))
      }

      // Server errors
      if (status >= 500) {
        return Promise.reject(
          new Error('Server error. Please try again later.')
        )
      }

      // Return error message from response, keeping the server's code and
      // status so a page can tell an expired link from a used one.
      const message = data?.error?.message || 'An error occurred'
      const err = new Error(message)
      err.status = status
      if (data?.error?.code) err.code = data.error.code
      if (data?.error) err.details = data.error
      return Promise.reject(err)
    }

    // Network error
    if (error.request) {
      return Promise.reject(
        new Error('Network error. Please check your connection.')
      )
    }

    // Other errors
    return Promise.reject(error)
  }
)

// Helper methods for common HTTP operations
export const apiClient = {
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  patch: (url, data, config) => api.patch(url, data, config),
  delete: (url, config) => api.delete(url, config),
}

export default api
